import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import userService, { getUserInfo, type SignInReq } from "@/api/services/userService";

import { toast } from "sonner";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";

import { jsonSafeParse, isNullLike, getErrorMsg } from '@/utils/fns';

const { VITE_APP_HOMEPAGE: HOMEPAGE } = import.meta.env;

export class UserStorage<T = any> {
  key!: string;
  uidGetter!: () => string;

  constructor(key: string, uidGetter: () => string) {
    this.key = key;
    this.uidGetter = uidGetter;
  }

  get() {
    const whole = jsonSafeParse<Record<string, T>>(
      localStorage.getItem(this.key),
      {},
    );
    const uid = this.uidGetter();
    return whole?.[uid];
  }

  set(val: T | null | undefined) {
    const whole =
      jsonSafeParse<Record<string, T>>(localStorage.getItem(this.key), {}) ||
      {};
    const uid = this.uidGetter();
    if (!isNullLike(val)) whole[uid] = val;
    else delete whole[uid];
    return localStorage.setItem(this.key, JSON.stringify(whole));
  }

  remove() {
    this.set(null);
  }
}

export const AuthToken = new UserStorage<string>('auth-token', () => 'cur');

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	// 使用 actions 命名空间来存放所有的 action
	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		clearUserInfoAndToken: () => void;
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set) => ({
			userInfo: {},
			userToken: {},
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				clearUserInfoAndToken() {
					set({ userInfo: {}, userToken: {} });
				},
			},
		}),
		{
			name: "userStore", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useUserActions = () => useUserStore((state) => state.actions);

export const useSignIn = () => {
	const navigate = useNavigate();
	const { setUserToken, setUserInfo } = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			setUserInfo({ username: data.username, id: 0 });
			if (res.needMfa) return res;
			const { token } = res;
			AuthToken.set(token);
			setUserToken({ token });
			const userInfo = await getUserInfo();
			setUserInfo(userInfo);
			navigate(HOMEPAGE, { replace: true });
			toast.success("Sign in success!", {
				closeButton: true,
			});
		} catch (err) {
			// toast.error(err.message, {
			// 	position: "top-center",
			// });
			console.log('err:', getErrorMsg(err));
			
		}
	};

	return signIn;
};

export default useUserStore;
